/**
 * Step renderers extracted from CsvImportModal.tsx so the state-machine
 * orchestrator stays under the max-lines ceiling.
 */

import { AlertCircle, AlertTriangle, CheckCircle, Download, RefreshCw } from 'lucide-react'
import type { CaseEndpoint, ImportEndpointItem } from '../../../types/case-container'
import { VALID_DEVICE_TYPES } from './helpers'

const MAX_ITEMS_DISPLAYED = 20

interface BlockingError {
  row: number
  field: string
  value: string
  message: string
}

interface FixableWarning {
  row: number
  field: string
  originalValue: string
  suggestedValue: string
  message: string
}

interface ImportResult {
  imported: number
  skipped: number
  errors: { row: number; field: string; message: string }[]
}

interface BlockingErrorsStepProps {
  blockingErrors: BlockingError[]
  onDownloadLog: () => void
  onReset: () => void
  onClose: () => void
}

export function CsvImportBlockingErrorsStep({
  blockingErrors,
  onDownloadLog,
  onReset,
  onClose,
}: BlockingErrorsStepProps): React.ReactElement {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950/30">
        <div className="mb-3 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
          <p className="font-medium text-red-700 dark:text-red-400">
            Import cannot proceed - {blockingErrors.length} blocking error
            {blockingErrors.length > 1 ? 's' : ''} found
          </p>
        </div>
        <p className="mb-3 text-sm text-red-600 dark:text-red-400">
          Please fix these issues in your CSV file and try again.
        </p>
        <ul className="max-h-48 space-y-2 overflow-y-auto text-sm text-red-600 dark:text-red-400">
          {blockingErrors.slice(0, MAX_ITEMS_DISPLAYED).map((err, i) => (
            <li
              key={i}
              className="flex items-start gap-2 rounded bg-red-100 p-2 dark:bg-red-900/30"
            >
              <span className="shrink-0 rounded bg-red-200 px-1 font-mono text-xs dark:bg-red-800">
                Row {err.row}
              </span>
              <div>
                <span className="font-medium">{err.field}:</span> {err.message}
                {err.value !== '(empty)' && (
                  <span className="text-red-500 dark:text-red-500">
                    {' '}
                    (value: &quot;{err.value}&quot;)
                  </span>
                )}
              </div>
            </li>
          ))}
          {blockingErrors.length > MAX_ITEMS_DISPLAYED && (
            <li className="text-red-500">
              ...and {blockingErrors.length - MAX_ITEMS_DISPLAYED} more errors
            </li>
          )}
        </ul>
      </div>

      <div className="flex justify-between pt-4">
        <button
          onClick={onDownloadLog}
          className="inline-flex items-center gap-2 rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-950/50"
        >
          <Download className="h-4 w-4" />
          Download Error Log
        </button>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onReset}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Select Different File
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-slate-600 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

interface ConfirmFixesStepProps {
  fixableWarnings: FixableWarning[]
  preview: ImportEndpointItem[]
  totalEndpoints: number
  onReset: () => void
  onImport: () => void
}

function FixableWarningsBanner({
  fixableWarnings,
}: {
  fixableWarnings: FixableWarning[]
}): React.ReactElement {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30">
      <div className="mb-3 flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        <p className="font-medium text-amber-700 dark:text-amber-400">
          {fixableWarnings.length} issue{fixableWarnings.length > 1 ? 's' : ''} can be automatically
          fixed
        </p>
      </div>
      <p className="mb-3 text-sm text-amber-600 dark:text-amber-400">
        The following changes will be made during import. Review and confirm to proceed.
      </p>
      <ul className="max-h-48 space-y-2 overflow-y-auto text-sm text-amber-600 dark:text-amber-400">
        {fixableWarnings.slice(0, MAX_ITEMS_DISPLAYED).map((warning, idx) => (
          <li
            key={idx}
            className="flex items-start gap-2 rounded bg-amber-100 p-2 dark:bg-amber-900/30"
          >
            <span className="shrink-0 rounded bg-amber-200 px-1 font-mono text-xs dark:bg-amber-800">
              Row {warning.row}
            </span>
            <div>
              <span className="text-amber-500 line-through">{warning.originalValue}</span>
              <span className="mx-2">&rarr;</span>
              <span className="font-medium text-amber-700 dark:text-amber-300">
                {warning.suggestedValue}
              </span>
            </div>
          </li>
        ))}
        {fixableWarnings.length > MAX_ITEMS_DISPLAYED && (
          <li className="text-amber-500">
            ...and {fixableWarnings.length - MAX_ITEMS_DISPLAYED} more changes
          </li>
        )}
      </ul>
    </div>
  )
}

function FixablePreviewTable({
  preview,
  totalEndpoints,
}: {
  preview: ImportEndpointItem[]
  totalEndpoints: number
}): React.ReactElement | null {
  if (preview.length === 0) return null
  return (
    <div>
      <h4 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
        Preview (first {preview.length} of {totalEndpoints} valid rows)
      </h4>
      <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400">
                Hostname
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400">
                Type
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400">
                IP
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {preview.map((item, i) => {
              const hasWarning = !VALID_DEVICE_TYPES.has(item.deviceType.toLowerCase())
              return (
                <tr key={i} className={hasWarning ? 'bg-amber-50 dark:bg-amber-950/20' : ''}>
                  <td className="px-3 py-2 text-slate-900 dark:text-slate-100">{item.hostname}</td>
                  <td
                    className={`px-3 py-2 ${hasWarning ? 'text-amber-600 dark:text-amber-400' : 'text-slate-600 dark:text-slate-400'}`}
                  >
                    {item.deviceType}
                    {hasWarning && <span className="ml-1 text-xs">(will fix)</span>}
                  </td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                    {item.ipAddresses?.[0] ?? '-'}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function CsvImportConfirmFixesStep({
  fixableWarnings,
  preview,
  totalEndpoints,
  onReset,
  onImport,
}: ConfirmFixesStepProps): React.ReactElement {
  return (
    <div className="space-y-4">
      <FixableWarningsBanner fixableWarnings={fixableWarnings} />
      <FixablePreviewTable preview={preview} totalEndpoints={totalEndpoints} />
      <div className="flex justify-end gap-3 pt-4">
        <button
          type="button"
          onClick={onReset}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          Select Different File
        </button>
        <button
          onClick={onImport}
          className="inline-flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white hover:bg-[#5c6e94]"
        >
          Apply Fixes &amp; Import ({totalEndpoints} endpoints)
        </button>
      </div>
    </div>
  )
}

interface PreviewStepProps {
  preview: ImportEndpointItem[]
  totalEndpoints: number
  error: string | null
  onReset: () => void
  onImport: () => void
}

function PreviewTable({
  preview,
  totalEndpoints,
}: {
  preview: ImportEndpointItem[]
  totalEndpoints: number
}): React.ReactElement | null {
  if (preview.length === 0) return null
  return (
    <div>
      <h4 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
        Preview (first {preview.length} of {totalEndpoints} rows)
      </h4>
      <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400">
                Hostname
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400">
                Type
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400">
                IP
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {preview.map((item, i) => (
              <tr key={i}>
                <td className="px-3 py-2 text-slate-900 dark:text-slate-100">{item.hostname}</td>
                <td className="px-3 py-2 text-slate-600 dark:text-slate-400">{item.deviceType}</td>
                <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                  {item.ipAddresses?.[0] ?? '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function CsvImportPreviewStep({
  preview,
  totalEndpoints,
  error,
  onReset,
  onImport,
}: PreviewStepProps): React.ReactElement {
  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}
      <div className="rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-800 dark:bg-green-950/30">
        <div className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
          <p className="text-sm font-medium text-green-700 dark:text-green-400">
            File validated successfully - {totalEndpoints} endpoints ready to import
          </p>
        </div>
      </div>
      <PreviewTable preview={preview} totalEndpoints={totalEndpoints} />
      <div className="flex justify-end gap-3 pt-4">
        <button
          type="button"
          onClick={onReset}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          Select Different File
        </button>
        <button
          onClick={onImport}
          className="inline-flex items-center gap-2 rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white hover:bg-[#5c6e94]"
        >
          Import ({totalEndpoints} endpoints)
        </button>
      </div>
    </div>
  )
}

interface SuccessStepProps {
  result: ImportResult
  verificationRecords: CaseEndpoint[]
  loadingVerification: boolean
  onClose: () => void
}

function ImportSummaryBanner({ result }: { result: ImportResult }): React.ReactElement {
  return (
    <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-950/30">
      <div className="mb-3 flex items-center gap-2">
        <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
        <p className="text-lg font-medium text-green-700 dark:text-green-400">Import Successful</p>
      </div>
      <div className="grid grid-cols-3 gap-4 text-sm">
        <div className="rounded bg-white/50 p-3 text-center dark:bg-slate-800/50">
          <div className="text-3xl font-bold text-green-600 dark:text-green-400">
            {result.imported}
          </div>
          <div className="text-slate-600 dark:text-slate-400">Imported</div>
        </div>
        <div className="rounded bg-white/50 p-3 text-center dark:bg-slate-800/50">
          <div className="text-3xl font-bold text-amber-600 dark:text-amber-400">
            {result.skipped}
          </div>
          <div className="text-slate-600 dark:text-slate-400">Skipped</div>
        </div>
        <div className="rounded bg-white/50 p-3 text-center dark:bg-slate-800/50">
          <div className="text-3xl font-bold text-red-600 dark:text-red-400">
            {result.errors.length}
          </div>
          <div className="text-slate-600 dark:text-slate-400">Errors</div>
        </div>
      </div>
    </div>
  )
}

function VerificationContent({
  verificationRecords,
  loadingVerification,
}: {
  verificationRecords: CaseEndpoint[]
  loadingVerification: boolean
}): React.ReactElement {
  if (loadingVerification) {
    return (
      <div className="flex items-center justify-center gap-2 p-4">
        <RefreshCw className="h-4 w-4 animate-spin text-slate-400" />
        <span className="text-sm text-slate-500">Loading verification...</span>
      </div>
    )
  }
  if (verificationRecords.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-slate-500 dark:text-slate-400">
        No recently imported records found to display.
      </div>
    )
  }
  return (
    <table className="w-full text-sm">
      <thead className="bg-slate-50 dark:bg-slate-800/50">
        <tr>
          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400">
            Hostname
          </th>
          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400">
            Type
          </th>
          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400">
            IP
          </th>
          <th className="px-3 py-2 text-left text-xs font-medium text-slate-500 dark:text-slate-400">
            Created
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
        {verificationRecords.map((record) => (
          <tr key={record.id}>
            <td className="px-3 py-2 font-medium text-slate-900 dark:text-slate-100">
              {record.hostname}
            </td>
            <td className="px-3 py-2 text-slate-600 dark:text-slate-400">{record.deviceType}</td>
            <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
              {record.ipAddresses?.[0] ?? '-'}
            </td>
            <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-500">
              {new Date(record.createdAt).toLocaleString()}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function CsvImportSuccessStep({
  result,
  verificationRecords,
  loadingVerification,
  onClose,
}: SuccessStepProps): React.ReactElement {
  return (
    <div className="space-y-4">
      <ImportSummaryBanner result={result} />
      <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-2 dark:border-slate-700 dark:bg-slate-800">
          <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Database Verification - Recently Imported Records
          </h4>
        </div>
        <VerificationContent
          verificationRecords={verificationRecords}
          loadingVerification={loadingVerification}
        />
      </div>
      <div className="flex justify-end pt-4">
        <button
          onClick={onClose}
          className="rounded-lg bg-[#4e5e82] px-4 py-2 text-sm font-medium text-white hover:bg-[#5c6e94]"
        >
          Done
        </button>
      </div>
    </div>
  )
}
